import { localInstantToUtc } from "./localInstantToUtc";

/**
 * Pure parser for the PRD-001 filter-mode inputs (extracted from
 * `DateFilterInput` so the validity predicate is directly unit-testable —
 * see requirements/test-setup.md, "extract pure helpers to sibling modules").
 *
 * The filter mode (PRD-001 §51, rev 12) accepts two independently-optional
 * constraints: a date+time+timezone constraint and a single free-text label.
 * **At least one of the two must be provided.** When both are present they are
 * later ANDed on the subject-search call.
 *
 * Label handling (PRD §51 + §52): the label is passed **verbatim** — no
 * case-folding, no normalization of the value sent. `trim()` is used ONLY as a
 * presence predicate (a whitespace-only entry is treated as "not provided" so
 * we never send an empty `labels=` param the backend could read as match-all);
 * the value carried in `label` is the original, untrimmed input.
 */
export interface FilterInputs {
    date: string; // YYYY-MM-DD (or "" when unset)
    time: string; // HH:MM or HH:MM:SS (or "" when unset)
    timeZone: string;
    label: string; // raw user input (or "" when unset)
}

export interface ParsedFilter {
    /** True when at least one valid constraint (date and/or label) is present. */
    ok: boolean;
    /**
     * The date constraint resolved to an RFC 3339 UTC instant (e.g.
     * `2026-01-15T17:00:00Z`). Present only when a valid date was supplied;
     * omitted for a label-only filter.
     */
    createdStartUtc?: string;
    /** The verbatim label string. Present only when a non-empty label was supplied. */
    label?: string;
}

export function parseFilterInputs(input: FilterInputs): ParsedFilter {
    // Label: verbatim value, trimmed-non-empty presence check.
    const label = input.label.trim() !== "" ? input.label : undefined;

    let createdStartUtc: string | undefined;
    if (input.date) {
        const dateMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(input.date);
        // A present-but-malformed date is a hard invalid — we do not silently
        // drop it and proceed label-only, which would surprise the user.
        if (!dateMatch) return { ok: false };
        let hour = 0;
        let minute = 0;
        if (input.time) {
            // Time picker may emit "HH:MM" or "HH:MM:SS" depending on browser;
            // accept either and treat anything past minute as zero.
            const timeMatch = /^(\d{2}):(\d{2})(?::\d{2})?$/.exec(input.time);
            if (!timeMatch) return { ok: false };
            hour = Number(timeMatch[1]);
            minute = Number(timeMatch[2]);
        }
        try {
            const utc = localInstantToUtc({
                year: Number(dateMatch[1]),
                month: Number(dateMatch[2]),
                day: Number(dateMatch[3]),
                hour,
                minute,
                timeZone: input.timeZone,
            });
            // RFC 3339 UTC with `Z`, no milliseconds (PRD §52 shape).
            createdStartUtc = utc.toISOString().replace(/\.\d{3}Z$/, "Z");
        } catch {
            return { ok: false };
        }
    }

    // PRD §51: at least one of {date, label} must be present.
    const ok = createdStartUtc !== undefined || label !== undefined;
    return { ok, createdStartUtc, label };
}
